import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './index.less';

/**
 * 自定义 Dropdown 组件 - 使用 Portal 渲染到 body，避免被 overflow 遮挡
 * @param {Object} props
 * @param {ReactNode} props.children - 触发下拉菜单的元素
 * @param {Array} props.items - 菜单项数组 [{ key, label, onClick, style, danger }]
 * @param {boolean} props.visible - 是否显示菜单
 * @param {Function} props.onVisibleChange - 显示状态变化回调
 * @param {string} props.trigger - 触发方式 'click' | 'hover'
 */
const Dropdown = (props) => {
  const {
    children,
    items = [],
    visible = false,
    onVisibleChange,
    trigger = 'click',
  } = props;

  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // 计算菜单位置
  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    setPosition({
      top: rect.bottom + scrollTop + 4, // 距离触发元素下方 4px
      left: rect.left + scrollLeft,
    });
  };

  // 显示时计算位置 & 监听外部点击
  useEffect(() => {
    if (visible) {
      calculatePosition();

      // 点击外部关闭
      const handleClickOutside = (e) => {
        const menuEl = document.querySelector('.custom-dropdown-menu');
        const triggerEl = triggerRef.current;

        if (
          menuEl &&
          triggerEl &&
          !menuEl.contains(e.target) &&
          !triggerEl.contains(e.target)
        ) {
          onVisibleChange?.(false);
        }
      };

      // 延迟添加监听，避免当前点击事件立即触发关闭
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [visible, onVisibleChange]);

  // 处理点击
  const handleClick = (e) => {
    e.stopPropagation(); // 阻止冒泡到父元素（避免触发会话选中）
    if (trigger === 'click') {
      onVisibleChange?.(!visible);
    }
  };

  // 处理鼠标移入移出（hover 触发）
  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      onVisibleChange?.(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      onVisibleChange?.(false);
    }
  };

  // 点击菜单项
  const handleItemClick = (item, e) => {
    e.stopPropagation();
    item.onClick?.(e);
    onVisibleChange?.(false);
  };

  // 渲染菜单
  const renderMenu = () => {
    if (!visible) return null;

    return (
      <div
        className="custom-dropdown-menu"
        style={{
          position: 'absolute',
          top: position.top,
          left: position.left,
          zIndex: 1050,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((item) => (
          <div
            key={item.key}
            className={`custom-dropdown-item ${item.danger ? 'danger' : ''}`}
            style={item.style}
            onClick={(e) => handleItemClick(item, e)}
          >
            {item.label}
          </div>
        ))}
      </div>
    );
  };

  // Portal 容器
  const portalContainer = document.body;

  return (
    <>
      <div
        ref={triggerRef}
        className="custom-dropdown-trigger"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {ReactDOM.createPortal(renderMenu(), portalContainer)}
    </>
  );
};

export default Dropdown;
